import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Drawer, Empty, Grid, Input, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, MessageOutlined, ReloadOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../../../../config/axiosConfig';
import './FlowResponsesPage.css';

const { Title, Text } = Typography;

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const parseJsonIfPossible = (value) => {
  if (isPlainObject(value) || Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const toDisplayText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatFlowResponseLabel = (key = '') => String(key || '')
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .trim()
  .replace(/\b\w/g, char => char.toUpperCase());

const flattenFlowResponseValues = (value, prefix = '') => {
  if (!isPlainObject(value) && !Array.isArray(value)) {
    return prefix ? [[prefix, value]] : [];
  }

  return Object.entries(value).flatMap(([key, item]) => {
    if (item === undefined || item === null || item === '') return [];
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(item) || Array.isArray(item)) {
      const nested = flattenFlowResponseValues(item, nextPrefix);
      return nested.length ? nested : [[nextPrefix, item]];
    }
    return [[nextPrefix, item]];
  });
};

const getFlowResponseFields = (row = {}) => {
  if (Array.isArray(row.fields)) {
    return row.fields.map((field, index) => ({
      key: field.key || field.name || `field_${index}`,
      label: field.label || formatFlowResponseLabel(field.key || field.name || `field_${index}`),
      value: field.value,
      displayValue: toDisplayText(field.displayValue ?? field.value),
    }));
  }

  const response = row.response || row.response_json || row.responseJson || {};
  const parsedResponse = parseJsonIfPossible(response) || response;
  return flattenFlowResponseValues(parsedResponse).map(([key, value]) => ({
    key,
    label: formatFlowResponseLabel(key),
    value,
    displayValue: toDisplayText(value),
  }));
};

const getFlowResponseSummary = (row = {}) => {
  const fields = getFlowResponseFields(row).filter(field => field.displayValue);
  if (!fields.length) return row.flowBody || row.flow_body || 'Submitted';
  return fields.slice(0, 3).map(field => field.displayValue).join(' | ');
};

const getFlowResponseRowId = (row = {}) => (
  row.id || row.responseId || row.response_id || row.conversationId || row.conversationid || row.wamid || row.originalMessageWamid || row.contactPhone
);

const formatFlowResponseDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function FlowResponsesPage() {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();

  // ── view state ───────────────────────────────────────────────────────────
  const [view, setView] = useState('grouped'); // 'grouped' | 'responses'
  const [activeGroup, setActiveGroup] = useState(null); // { templateName, campaignId, campaignName, flowName, totalResponses }

  // ── grouped view state ───────────────────────────────────────────────────
  const [groupedRows, setGroupedRows] = useState([]);
  const [groupedLoading, setGroupedLoading] = useState(false);
  const [groupedSearch, setGroupedSearch] = useState('');
  const [groupedPagination, setGroupedPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const groupedSearchRef = useRef('');

  // ── individual responses state ───────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const searchRef = useRef('');

  // ── fetch grouped ────────────────────────────────────────────────────────
  const fetchGrouped = useCallback(async ({ page, pageSize, nextSearch } = {}) => {
    const currentPage = page || groupedPagination.current;
    const currentPageSize = pageSize || groupedPagination.pageSize;
    const currentSearch = nextSearch !== undefined ? nextSearch : groupedSearchRef.current;

    setGroupedLoading(true);
    try {
      const response = await axiosConfig.get('/inbox/flow-responses/grouped', {
        params: {
          limit: currentPageSize,
          offset: (currentPage - 1) * currentPageSize,
          ...(currentSearch ? { search: currentSearch } : {}),
        },
      });

      const responseData = response.data || {};
      const dataRows = Array.isArray(responseData.data)
        ? responseData.data
        : responseData.data?.items
          || responseData.entity?.data
          || responseData.entity?.items
          || responseData.entity
          || [];
      const meta = responseData.meta || responseData.data?.meta || responseData.entity?.meta || {};
      const total = meta.total ?? meta.count ?? responseData.data?.total ?? responseData.entity?.total ?? dataRows.length;

      setGroupedRows(Array.isArray(dataRows) ? dataRows : []);
      setGroupedPagination(prev => ({ ...prev, current: currentPage, pageSize: currentPageSize, total }));
    } catch (err) {
      console.error('Failed to fetch grouped Flow responses:', err);
      message.error(err.response?.data?.message || 'Could not load grouped responses');
    } finally {
      setGroupedLoading(false);
    }
  }, [groupedPagination.current, groupedPagination.pageSize]);

  // ── fetch individual responses ───────────────────────────────────────────
  const fetchFlowResponses = useCallback(async ({ page, pageSize, nextSearch, templateName, campaignId } = {}) => {
    const currentPage = page || pagination.current;
    const currentPageSize = pageSize || pagination.pageSize;
    const currentSearch = nextSearch !== undefined ? nextSearch : searchRef.current;

    setLoading(true);
    try {
      const response = await axiosConfig.get('/inbox/flow-responses', {
        params: {
          limit: currentPageSize,
          offset: (currentPage - 1) * currentPageSize,
          ...(currentSearch ? { search: currentSearch } : {}),
          ...(templateName ? { templateName } : {}),
          ...(campaignId ? { campaignId } : {}),
        },
      });

      const responseData = response.data || {};
      const dataRows = Array.isArray(responseData.data)
        ? responseData.data
        : responseData.data?.items
          || responseData.data?.responses
          || responseData.entity?.data
          || responseData.entity?.items
          || responseData.entity?.responses
          || responseData.entity
          || [];
      const meta = responseData.meta || responseData.data?.meta || responseData.entity?.meta || {};
      const total = meta.total ?? meta.count ?? responseData.data?.total ?? responseData.entity?.total ?? dataRows.length;

      setRows(Array.isArray(dataRows) ? dataRows : []);
      setPagination(prev => ({ ...prev, current: currentPage, pageSize: currentPageSize, total }));
    } catch (err) {
      console.error('Failed to fetch Flow responses:', err);
      message.error(err.response?.data?.message || 'Could not load Flow responses');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchGrouped({ page: 1, pageSize: groupedPagination.pageSize });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers ─────────────────────────────────────────────────────────────
  const handleGroupedSearch = () => {
    const nextSearch = groupedSearch.trim();
    groupedSearchRef.current = nextSearch;
    fetchGrouped({ page: 1, pageSize: groupedPagination.pageSize, nextSearch });
  };

  const handleSearch = () => {
    const nextSearch = search.trim();
    searchRef.current = nextSearch;
    fetchFlowResponses({ page: 1, pageSize: pagination.pageSize, nextSearch, templateName: activeGroup?.templateName, campaignId: activeGroup?.campaignId });
  };

  const handleDrillDown = (group) => {
    setActiveGroup(group);
    setSearch('');
    searchRef.current = '';
    setRows([]);
    setPagination({ current: 1, pageSize: 50, total: 0 });
    setView('responses');
    fetchFlowResponses({ page: 1, pageSize: 50, nextSearch: '', templateName: group.templateName, campaignId: group.campaignId });
  };

  const handleBackToGrouped = () => {
    setView('grouped');
    setActiveGroup(null);
    setDrawer({ open: false, row: null });
  };

  // ── column definitions ───────────────────────────────────────────────────
  const groupedColumns = [
    {
      title: '#',
      key: '__srno',
      width: 48,
      render: (_, __, index) => (
        <span className="frp-cell-srno">
          {(groupedPagination.current - 1) * groupedPagination.pageSize + index + 1}
        </span>
      ),
    },
    {
      title: 'Template',
      key: 'templateName',
      render: (_, row) => (
        <div>
          <span className="frp-cell-template-name">{row.templateName || row.template_name || '-'}</span>
          <span className="frp-cell-flow-sub">{row.flowName || row.flow_name || ''}</span>
        </div>
      ),
    },
    {
      title: 'Campaign',
      key: 'campaignName',
      render: (_, row) => row.campaignName || row.campaign_name || row.campaignId || row.campaign_id || '-',
    },
    {
      title: 'Responses',
      key: 'totalResponses',
      width: 110,
      align: 'center',
      render: (_, row) => (
        <Tag color="blue" className="frp-count-tag">
          {(row.totalResponses ?? row.total_responses ?? 0).toLocaleString()}
        </Tag>
      ),
    },
    {
      title: 'Last Response',
      key: 'lastResponseAt',
      width: 200,
      render: (_, row) => formatFlowResponseDateTime(row.lastResponseAt || row.last_response_at),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      fixed: 'right',
      align: 'center',
      render: (_, row) => (
        <button
          className="frp-view-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleDrillDown({
              templateName: row.templateName || row.template_name,
              campaignId: row.campaignId || row.campaign_id,
              campaignName: row.campaignName || row.campaign_name,
              flowName: row.flowName || row.flow_name,
              totalResponses: row.totalResponses || row.total_responses || 0,
            });
          }}
        >
          View Responses <RightOutlined className="frp-view-btn-icon" />
        </button>
      ),
    },
  ];

  const responseColumns = [
    {
      title: 'Submitted at',
      key: 'submittedAt',
      width: 170,
      render: (_, row) => formatFlowResponseDateTime(row.submittedAt || row.submitted_at || row.createdAt || row.created_at),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_, row) => (
        <div>
          <span className="frp-cell-name">{row.contactName || row.contact_name || 'Unknown'}</span>
          <span className="frp-cell-phone">{row.contactPhone || row.contact_phone || '-'}</span>
        </div>
      ),
    },
    { title: 'Summary', key: 'summary', ellipsis: true, render: (_, row) => getFlowResponseSummary(row) },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, row) => (
        <Tooltip title="Open details">
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDrawer({ open: true, row })} />
        </Tooltip>
      ),
    },
  ];

  const selectedRow = drawer.row;
  const selectedFields = selectedRow ? getFlowResponseFields(selectedRow) : [];
  const selectedRawResponse = selectedRow?.response || selectedRow?.response_json || selectedRow?.responseJson || {};

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="frp-page">

      {/* ── VIEW 1: Grouped ── */}
      {view === 'grouped' && (
        <>
          <div className="frp-topbar">
            <div className="frp-topbar-left">
              <div>
                <p className="frp-title">Flow Responses</p>
                <p className="frp-subtitle">WhatsApp Flow responses grouped by template &amp; campaign</p>
              </div>
            </div>
            <div className="frp-topbar-right">
              <Input
                allowClear
                size="small"
                placeholder="Search template or campaign..."
                prefix={<SearchOutlined />}
                value={groupedSearch}
                onChange={(e) => setGroupedSearch(e.target.value)}
                onPressEnter={handleGroupedSearch}
                style={{ width: 240, borderRadius: 7 }}
              />
              <Button size="small" type="primary" icon={<SearchOutlined />} onClick={handleGroupedSearch}>Search</Button>
              <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchGrouped()} loading={groupedLoading}>Refresh</Button>
            </div>
          </div>

          <div className="frp-table-wrap">
            <Table
              className="frp-table"
              size="small"
              rowKey={(row, index) => `${row.templateName || row.template_name}-${row.campaignId || row.campaign_id}-${index}`}
              columns={groupedColumns}
              dataSource={groupedRows}
              loading={groupedLoading}
              scroll={{ x: 700, y: 'calc(100vh - 210px)' }}
              onRow={(row) => ({
                className: 'frp-row-clickable',
                onClick: () => handleDrillDown({
                  templateName: row.templateName || row.template_name,
                  campaignId: row.campaignId || row.campaign_id,
                  campaignName: row.campaignName || row.campaign_name,
                  flowName: row.flowName || row.flow_name,
                  totalResponses: row.totalResponses || row.total_responses || 0,
                }),
              })}
              pagination={{
                current: groupedPagination.current,
                pageSize: groupedPagination.pageSize,
                total: groupedPagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['25', '50', '100'],
                showTotal: total => `${total} template groups`,
                onChange: (page, pageSize) => fetchGrouped({ page, pageSize }),
              }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Flow responses found" /> }}
            />
          </div>
        </>
      )}

      {/* ── VIEW 2: Individual Responses ── */}
      {view === 'responses' && (
        <>
          <div className="frp-topbar">
            <div className="frp-topbar-left">
              <Button
                className="frp-back-btn"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToGrouped}
              />
              <div>
                <p className="frp-title">
                  {activeGroup?.templateName || 'Responses'}
                  {activeGroup?.campaignName && (
                    <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 6 }}>— {activeGroup.campaignName}</span>
                  )}
                </p>
                <p className="frp-subtitle">
                  {activeGroup?.totalResponses ? `${activeGroup.totalResponses.toLocaleString()} responses` : 'Submitted responses'}
                  {activeGroup?.flowName ? ` · ${activeGroup.flowName}` : ''}
                </p>
              </div>
            </div>
            <div className="frp-topbar-right">
              <Input
                allowClear
                size="small"
                placeholder="Search phone, name..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 220, borderRadius: 7 }}
              />
              <Button size="small" type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => fetchFlowResponses({ page: 1, pageSize: pagination.pageSize, templateName: activeGroup?.templateName, campaignId: activeGroup?.campaignId })}
              >
                Refresh
              </Button>
            </div>
          </div>

          <div className="frp-table-wrap">
            <Table
              className="frp-table"
              size="small"
              rowKey={(row, index) => getFlowResponseRowId(row) || `flow-response-${index}`}
              columns={responseColumns}
              dataSource={rows}
              loading={loading}
              scroll={{ x: 600, y: 'calc(100vh - 210px)' }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['25', '50', '100', '200'],
                showTotal: total => `${total} responses`,
                onChange: (page, pageSize) => fetchFlowResponses({ page, pageSize, templateName: activeGroup?.templateName, campaignId: activeGroup?.campaignId }),
              }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No responses found" /> }}
            />
          </div>
        </>
      )}

      {/* ── Detail Drawer (unchanged) ── */}
      <Drawer
        title={selectedRow ? `${selectedRow.contactName || selectedRow.contact_name || 'Customer'} - Flow response` : 'Flow response'}
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        width={screens.xs ? '100%' : 620}
      >
        {selectedRow && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Submitted fields</Text>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {selectedFields.length > 0 ? selectedFields.map(field => (
                    <tr key={field.key}>
                      <th style={{ width: 180, textAlign: 'left', verticalAlign: 'top', padding: '8px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                        {field.label}
                      </th>
                      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', wordBreak: 'break-word' }}>
                        {field.displayValue || '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td style={{ padding: 12, border: '1px solid #e5e7eb', color: '#64748b' }}>No submitted fields</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setDrawer({ open: false, row: null })}>Close</Button>
              <Button type="primary" icon={<MessageOutlined />} onClick={() => navigate('/dashboard/inbox')}>
                Open inbox
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
